if (!customElements.get("m-product-tabs")) {
  class MProductTabs extends HTMLElement {
    constructor() {
      super();
      this.selectors = {
        loadMoreBtn: "[data-load-more-product]",
        productsContainer: "[data-products-container]",
        tabContent: [".m-tab-content"],
      };
      this.tabSliders = [];
      this.sliderEnabled = this.dataset.enableSlider === "true";
      this.mobileSliderDisable = this.dataset.mobileDisableSlider === "true";
      this.buttonType = this.dataset.buttonType;
      this.domNodes = queryDomNodes(this.selectors, this);
      this.showPagination = this.dataset.showPagination === "true";
      this.showNavigation = this.dataset.showNavigation === "true";
      this.items = this.dataset.items;
      this.initialized = false;
      this.viewportObserver = null;
      this.hoverImageEventsInitialized = false;
      this.deferredCardEventsInitialized = false;
    }

    connectedCallback() {
      this.scheduleInit();
    }

    disconnectedCallback() {
      if (this.viewportObserver) {
        this.viewportObserver.disconnect();
        this.viewportObserver = null;
      }
    }

    scheduleInit() {
      if (!this.shouldDeferInit()) {
        this.init();
        return;
      }

      this.viewportObserver = new IntersectionObserver((entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          this.viewportObserver.disconnect();
          this.viewportObserver = null;
          this.init();
        }
      }, { rootMargin: "250px 0px" });

      this.viewportObserver.observe(this);
    }

    shouldDeferInit() {
      if (this.initialized || Shopify.designMode || !("IntersectionObserver" in window)) return false;

      const rect = this.getBoundingClientRect();
      return rect.top > window.innerHeight + 250;
    }

    init() {
      if (this.initialized) return;
      this.initialized = true;

      if (this.sliderEnabled) {
        for (let block of this.domNodes.tabContent) {
          this.initSliderByScreenSize(block);
          document.addEventListener("matchMobile", () => {
            this.initSliderByScreenSize(block);
          });
          document.addEventListener("unmatchMobile", () => {
            this.initSliderByScreenSize(block);
          });
        }
      }
      if (!this.sliderEnabled && this.buttonType === "load") {
        this.canLoad = true;
        this.currentPage = 1;

        for (let block of this.domNodes.tabContent) {
          this.initLoadMore(block);
        }
      }
      if (MinimogTheme.config.mqlMobile && this.mobileSliderDisable && this.buttonType === "load") {
        this.canLoad = true;
        this.currentPage = 1;
        for (let block of this.domNodes.tabContent) {
          this.initLoadMore(block);
        }
      }
      document.addEventListener("matchMobile", () => {
        if (MinimogTheme.config.mqlMobile && this.mobileSliderDisable && this.buttonType === "load") {
          this.canLoad = true;
          this.currentPage = 1;
          for (let block of this.domNodes.tabContent) {
            this.initLoadMore(block);
          }
        }
      });
      this.initTabs();
      this.initMobileSelect();
      this.initDeferredProductCards();
      this.initDeferredHoverImages();

      if (Shopify.designMode) {
        document.addEventListener("shopify:block:select", (event) => {
          const blockSelectedIsTab = event.target.classList.contains("m-tab-content");
          if (!blockSelectedIsTab) return;
          const dataIndex = event.target && event.target.dataset.index;
          this.tabs.setActiveTab(dataIndex);
        });
      }
    }

    initDeferredProductCards() {
      if (this.deferredCardEventsInitialized) return;
      this.deferredCardEventsInitialized = true;

      if (!this.querySelector("template[data-product-tabs-deferred-card]")) return;

      const hydrate = () => this.hydrateDeferredProductCards();
      this.addEventListener("pointerdown", hydrate, { once: true, passive: true, capture: true });
      this.addEventListener("touchstart", hydrate, { once: true, passive: true, capture: true });
      this.addEventListener("keydown", hydrate, { once: true });
      this.addEventListener("focusin", hydrate, { once: true });
    }

    hydrateDeferredProductCards() {
      const templates = this.querySelectorAll("template[data-product-tabs-deferred-card]");
      if (!templates.length) return;

      templates.forEach((template) => {
        template.replaceWith(template.content.cloneNode(true));
      });

      this.querySelectorAll(".swiper-container").forEach((slider) => {
        if (slider.swiper) slider.swiper.update();
      });
    }

    initDeferredHoverImages() {
      if (this.hoverImageEventsInitialized) return;
      this.hoverImageEventsInitialized = true;

      const canHover = !window.matchMedia || window.matchMedia("(hover: hover) and (pointer: fine)").matches;
      if (!canHover) return;

      this.addEventListener("pointerover", (event) => {
        const card = event.target.closest(".m-product-card");
        if (card && this.contains(card)) this.loadDeferredHoverImages(card);
      });

      this.addEventListener("focusin", (event) => {
        const card = event.target.closest(".m-product-card");
        if (card && this.contains(card)) this.loadDeferredHoverImages(card);
      });
    }

    loadDeferredHoverImages(container) {
      container.querySelectorAll("[data-product-card-hover-lazy]").forEach((image) => {
        if (image.dataset.srcset) {
          image.srcset = image.dataset.srcset;
          image.removeAttribute("data-srcset");
        }

        if (image.dataset.src) {
          image.src = image.dataset.src;
          image.removeAttribute("data-src");
        }

        image.removeAttribute("data-product-card-hover-lazy");
      });
    }

    initTabs() {
      this.tabs = new MinimogTheme.Tabs(this, (target) => {
        const tabId = target.getAttribute("href");
        const slider = this.querySelector(tabId + " .swiper-container");
        const controlsContainer = this.querySelector(tabId + " .m-slider-controls");
        // trigger update slider
        slider && slider.swiper && slider.swiper.update();
        const firstItem = slider && (slider.querySelector(".m-image") || slider.querySelector(".m-placeholder-svg"));
        if (firstItem && controlsContainer) {
          const itemHeight = firstItem.clientHeight;
          controlsContainer.style.setProperty("--offset-top", parseInt(itemHeight) / 2 + "px");
        }
      });
    }

    initSliderByScreenSize(sliderContainer) {
      const mobileDisableSlider = this.dataset.mobileDisableSlider === "true";
      const slider = sliderContainer.querySelector(".m-mixed-layout__wrapper");
      const controlsContainer = sliderContainer.querySelector(".m-slider-controls");

      if (MinimogTheme.config.mqlMobile && mobileDisableSlider) {
        controlsContainer && controlsContainer.classList.add("m:hidden");
        slider.classList.remove("swiper-container");
        if (slider.swiper) slider.swiper.destroy(false, true);
      } else {
        controlsContainer && controlsContainer.classList.remove("m:hidden");
        setTimeout(() => {
          this.initSlider(sliderContainer);
        });
      }
    }

    initSlider(sliderContainer) {
      const layoutWrapper = sliderContainer.querySelector(".m-product-list");
      const swiper = sliderContainer && sliderContainer.querySelector(".m-mixed-layout__wrapper");
      const controlsContainer = sliderContainer.querySelector(".m-slider-controls");
      const prevButton = controlsContainer && controlsContainer.querySelector(".m-slider-controls__button-prev");
      const nextButton = controlsContainer && controlsContainer.querySelector(".m-slider-controls__button-next");
      const slideItemsLength =
        sliderContainer &&
        sliderContainer.querySelector(".swiper-wrapper") &&
        sliderContainer.querySelector(".swiper-wrapper").querySelectorAll(".swiper-slide").length;
      /* Only fall back to a static (non-swipeable) grid when there's truly
         nothing to slide through (0 or 1 product). The previous check
         compared against items_per_row — a DESKTOP "products per row"
         setting — so any tab whose product count happened to be <= that
         number (e.g. 5 products with "Products per row" set to 5) never
         got a Swiper instance at all, even on mobile, where only ~1.5
         cards show at once and swiping is clearly still needed. Swiper's
         own per-breakpoint slidesPerView (see below) already hides the
         nav controls on larger screens once everything fits, so nothing
         is lost by always initializing the slider when there's more than
         one item. */
      if (slideItemsLength <= 1) {
        controlsContainer && controlsContainer.classList.add("m:hidden");
        layoutWrapper.classList.add("m-mixed-layout--mobile-grid");
        return;
      }

      swiper && swiper.classList.add("swiper-container");

      // Gap is rendered via CSS (--column-gap / --column-gap-mobile), but Swiper's
      // own translate math (slidesPerView) has no idea it exists unless we tell it
      // via spaceBetween. Read the real values so the slide-to-slide step is
      // "slide width + gap" instead of just "slide width" (which left a tail).
      const layoutStyles = getComputedStyle(layoutWrapper);
      const gapMobile = parseFloat(layoutStyles.getPropertyValue("--column-gap-mobile")) || 0;
      const gapDesktop = parseFloat(layoutStyles.getPropertyValue("--column-gap")) || gapMobile;
      swiper.classList.add("m-swiper-space-between-active");

     let slider = new MinimogLibs.Swiper(swiper, {
  slidesPerView: 1.5,
  spaceBetween: gapMobile,
  roundLengths: true,
  showPagination: this.showPagination,
  showNavigation: this.showNavigation,
  loop: false,
  noSwiping: true,
  noSwipingSelector: '.lamel-card-swatches-wrapper, .lamel-card-swatches-viewport',
        pagination: this.showPagination
          ? {
              el: sliderContainer.querySelector(".swiper-pagination"),
              clickable: true,
            }
          : false,
        breakpoints: {
          768: {
            slidesPerView: parseInt(this.items) >= 3 ? 3 : parseInt(this.items),
            spaceBetween: gapDesktop,
          },
          992: {
            slidesPerView: parseInt(this.items) >= 4 ? 4 : parseInt(this.items),
            spaceBetween: gapDesktop,
          },
          1280: {
            slidesPerView: parseInt(this.items),
            spaceBetween: gapDesktop,
          },
        },
        threshold: 2,
        on: {
          init: function () {
            setTimeout(() => {
              // Calculate controls position
              const firstItem =
                sliderContainer.querySelector(".m-image") || sliderContainer.querySelector(".m-placeholder-svg");
              if (firstItem && controlsContainer) {
                const itemHeight = firstItem.clientHeight;
                controlsContainer.style.setProperty("--offset-top", parseInt(itemHeight) / 2 + "px");

                prevButton.classList.remove("m:hidden");
                nextButton.classList.remove("m:hidden");
              }
            }, 200);
          },
          breakpoint: (swiper, breakpointParams) => {
            if (controlsContainer) {
              const { slidesPerView } = breakpointParams;
              if (slideItemsLength > slidesPerView) {
                controlsContainer.classList.remove("m:hidden");
                swiper.allowTouchMove = true;
              } else {
                controlsContainer.classList.add("m:hidden");
                swiper.allowTouchMove = false;
              }
            }
          },
        },
      });

      if (slider && this.showNavigation) {
        prevButton && prevButton.addEventListener("click", () => slider.slidePrev());
        nextButton && nextButton.addEventListener("click", () => slider.slideNext());
      }
    }

    initMobileSelect() {
      this.select = this.querySelector("[data-tab-select]");
      this.select.addEventListener("change", () => {
        this.tabs.setActiveTab(parseInt(this.select.value));
        const slider = this.tabs && this.tabs.currentTab && this.tabs.currentTab.querySelector(".swiper-container");
        const controlsContainer =
          this.tabs && this.tabs.currentTab && this.tabs.currentTab.querySelector(".m-slider-controls");
        const prevButton = controlsContainer && controlsContainer.querySelector(".m-slider-controls__button-prev");
        const nextButton = controlsContainer && controlsContainer.querySelector(".m-slider-controls__button-next");
        slider && slider.swiper && slider.swiper.update();
        const firstItem = slider && slider.querySelector(".m-image");
        if (firstItem && controlsContainer) {
          const itemHeight = firstItem.clientHeight;
          controlsContainer.style.setProperty("--offset-top", parseInt(itemHeight) / 2 + "px");

          prevButton.classList.remove("m:hidden");
          nextButton.classList.remove("m:hidden");
        }
      });
    }

    initLoadMore(wrapper) {
      addEventDelegate({
        context: wrapper,
        selector: this.selectors.loadMoreBtn,
        handler: (e) => {
          e.preventDefault();
          this.handleLoadMore(wrapper);
        },
      });
    }

    handleLoadMore(wrapper) {
      const loadBtn = wrapper.querySelector(this.selectors.loadMoreBtn);
      const productsContainer = wrapper.querySelector(this.selectors.productsContainer);

      let currentPage = wrapper.dataset.page;
      currentPage = parseInt(currentPage);
      const totalPages = wrapper.dataset.totalPages;
      this.toggleLoading(loadBtn, true);

      const url = wrapper.dataset.url;
      const dataUrl = `${url}?page=${currentPage + 1}&section_id=${this.id}`;
      fetchCache(dataUrl).then((html) => {
        currentPage++;
        wrapper.dataset.page = currentPage;
        this.toggleLoading(loadBtn, false);
        const dom = generateDomFromString(html);
        const tabId = wrapper.getAttribute("id");
        const products = dom.querySelector(`#${tabId} ${this.selectors.productsContainer}`);

        if (products) {
          Array.from(products.childNodes).forEach((product) => productsContainer.appendChild(product));
        }

        if (currentPage >= parseInt(totalPages)) loadBtn && loadBtn.remove();
      });

      // Remove button focus
      loadBtn.blur();
    }

    toggleLoading(loadBtn, status) {
      if (!loadBtn) return;
      if (status) {
        loadBtn.classList.add("m-spinner-loading");
      } else {
        loadBtn.classList.remove("m-spinner-loading");
      }
    }
  }
  customElements.define("m-product-tabs", MProductTabs);
}